import type {
  RangedFieldTypes,
  ResultRecord,
} from '@dynatrace-sdk/client-query';

import { checkTimeseriesFormat } from './check-timeseries-format.js';
import { extractStartEndDates } from './extract-start-end-dates.js';
import { getDimensions, getValueDimensions } from './get-dimensions.js';
import {
  TimeseriesWithDimensions,
  createName,
} from '../convert-to-timeseries.js';
import { convertToFieldEntries } from '../getFieldsArrayForRecordIndex.js';
import { extractValue } from '../utils/extractValue.js';

/**
 * Creates timeseries from a query result.
 * @param record - The input records from the query
 * @param types - Types of fields
 */
export function createTimeseriesFromRecord(
  record: ResultRecord,
  types: RangedFieldTypes,
): TimeseriesWithDimensions[] {
  const timeseriesArray: TimeseriesWithDimensions[] = [];
  const timeseriesFields = checkTimeseriesFormat(types.mappings);

  if (!timeseriesFields.isTimeseries) {
    return timeseriesArray;
  }

  const fieldsArray = convertToFieldEntries(types);

  const dimensions = getDimensions(record, fieldsArray, true);
  const [start] = extractStartEndDates(record, fieldsArray);

  const valueDimensions = getValueDimensions(fieldsArray, true);

  for (const valueDimension of valueDimensions ?? []) {
    if (valueDimension.name === '') {
      throw new Error(`Could not calculate key for record ${record}`);
    }

    const timeseries: TimeseriesWithDimensions = {
      name: createName(dimensions, valueDimension.name),
      datapoints: [],
      userData: {
        dimensions: [
          ...dimensions,
          {
            field: {
              name: timeseriesFields.durationField,
              type: 'duration',
            },
            value: record[timeseriesFields.durationField],
          },

          {
            field: {
              name: timeseriesFields.timeframeField,
              type: 'timeframe',
            },
            value: record[timeseriesFields.timeframeField],
          },
        ],
        valueDimension: valueDimension.name,
      },
    };
    timeseriesArray.push(timeseries);

    const values = extractValue<Array<number | string | null>>(
      record,
      valueDimension.name,
    );
    // Timeseries contains duration in nanoseconds as a string. Convert it to integer milliseconds.
    const duration = Number(
      BigInt(
        extractValue<string>(record, timeseriesFields.durationField) as string,
      ) / BigInt(1000000),
    );

    if (values !== undefined) {
      let newStart = start;

      for (const value of values) {
        const newEnd = new Date(newStart.getTime() + duration);
        if (value !== null) {
          timeseries.datapoints.push({
            start: newStart,
            end: newEnd,
            value: typeof value === 'string' ? parseFloat(value) : value,
          });
        }

        newStart = newEnd;
      }
    }
  }
  return timeseriesArray;
}
